import { create } from 'zustand'

import {
  DEFAULT_THEME_PREFERENCE,
  ResolvedTheme,
  ThemePreference,
  applyTheme,
  readPreference,
  resolveTheme,
  systemPrefersDark,
  writePreference,
} from '@/lib/theme'

type ThemeState = {
  preference: ThemePreference
  resolved: ResolvedTheme
  hydrate: () => void
  setPreference: (preference: ThemePreference) => void
  syncFromSystem: (prefersDark: boolean) => void
}

// The initial state must match what the server rendered. The bootstrap script in
// _document.tsx has already put the class on <html> by the time React hydrates, but React
// itself must not render anything theme-dependent before hydrate() runs after mount —
// otherwise the markup diverges and hydration warns (or silently patches).
//
// The corollary for everything downstream: express the theme in CSS, never as
// `resolved === 'dark' ? <A/> : <B/>`.
const useThemeStore = create<ThemeState>((set, get) => ({
  preference: DEFAULT_THEME_PREFERENCE,
  resolved: 'light',

  hydrate: () => {
    const preference = readPreference()
    const resolved = resolveTheme(preference, systemPrefersDark())
    applyTheme(resolved)
    set({ preference, resolved })
  },

  setPreference: (preference) => {
    const resolved = resolveTheme(preference, systemPrefersDark())
    writePreference(preference)
    applyTheme(resolved)
    set({ preference, resolved })
  },

  syncFromSystem: (prefersDark) => {
    // Only 'system' follows the OS — an explicit choice outranks it.
    if (get().preference !== 'system') return
    const resolved = resolveTheme('system', prefersDark)
    applyTheme(resolved)
    set({ resolved })
  },
}))

export default useThemeStore
